import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, mergeProps as _mergeProps, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-a9a60910"),n=n(),_popScopeId(),n)
const _hoisted_1 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("thead", null, [
  /*#__PURE__*/_createElementVNode("tr", null, [
    /*#__PURE__*/_createElementVNode("th", null, "Order"),
    /*#__PURE__*/_createElementVNode("th", null, "Approvers"),
    /*#__PURE__*/_createElementVNode("th", null, "Actions")
  ])
], -1))
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("thead", null, [
  /*#__PURE__*/_createElementVNode("tr", null, [
    /*#__PURE__*/_createElementVNode("th", null, "Revision"),
    /*#__PURE__*/_createElementVNode("th", null, "Status"),
    /*#__PURE__*/_createElementVNode("th", null, "Created On"),
    /*#__PURE__*/_createElementVNode("th", null, "Actions")
  ])
], -1))

import { onBeforeUnmount, ref } from "vue";
import { useRouter } from "vue-router";

import type { FilterDefinition } from "@/types/filter";
import type { DataTableHeader } from "@/types/table";

import { AssetManagementPlanMaster, User } from "@/classes";

import { setupApiQueue } from "@/components/base/api-queue";
import {
  AssetManagementPlan,
  AssetManagementPlanMasterApprovalStage,
  AssetManagementPlanMasterApprover,
  AssetManagementPlanMasterNewDetails,
} from "@/classes/asset-management-plan";

import PaginatedTable from "@/components/base/tables/PaginatedTable.vue";
import BaseDialog from "@/components/base/dialogs/BaseDialog.vue";
import { useListStore } from "@/store/lists";
import { useConfirmStore } from "@/store/confirm";
import draggable from "vuedraggable";
import { useAuthStore } from "@/store/auth";
import axiosInstance from "@/plugins/axios";
import CompareRevisionsDialog from "@/components/asset-management-plan/diff/CompareRevisionsDialog.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'asset-management-plans',
  setup(__props) {

const $router = useRouter();
const $auth = useAuthStore();
const $confirm = useConfirmStore();
const lists = useListStore();

const users = lists.useList<User>("users");
onBeforeUnmount(() => {
  if ($auth.state.user) {
    lists.refreshList("assetManagementPlanMasters");
  }
});

const headers: DataTableHeader[] = [
  {
    title: "Name",
    key: "name",
    sortable: true,
  },
  {
    title: "Published Revision",
    key: "latest_published_revision_name",
    sortable: true,
  },
  {
    title: "Last Published Date",
    key: "latest_published_timestamp",
    sortable: true,
  },
  {
    title: "Current Revision",
    key: "latest_revision_name",
    sortable: true,
  },
  {
    title: "Current Revision Status",
    key: "latest_revision_status",
    sortable: true,
  },
  {
    title: "Action",
    key: "actions",
    sortable: false,
  },
];

const filterDefinitions: FilterDefinition[] = [
  {
    label: "Name",
    value: "name",
    type: "string",
  },
];

const table = ref<InstanceType<typeof PaginatedTable> | null>(null);

const { queueApiAction, loading } = setupApiQueue();

const newAssetManagementPlan = () => {
  queueApiAction(async () => {
    const master = await AssetManagementPlanMaster.newPlan(
      newPlanDetails.value,
    );
    newPlanDetails.value = { ...defaultDetails };
    $router.push(
      "/asset-management-plan?id=" + master.metrics.latest_revision_id,
    );
  });
};

const newDialog = ref(false);

const defaultDetails: AssetManagementPlanMasterNewDetails = { name: "" };
const newPlanDetails = ref<AssetManagementPlanMasterNewDetails>({
  ...defaultDetails,
});

const approvalStagesDialog = ref(false);
const currentPlanMaster = ref<AssetManagementPlanMaster>(
  new AssetManagementPlanMaster(),
);

const openApprovalStagesDialog = (planMaster: AssetManagementPlanMaster) => {
  approvalStagesDialog.value = true;
  currentPlanMaster.value = planMaster;
};

const addApprovalStage = () => {
  const newStage = new AssetManagementPlanMasterApprovalStage();
  newStage.order = currentPlanMaster.value.approval_stages.length;
  newStage.asset_management_plan_master_id = currentPlanMaster.value.id;
  currentPlanMaster.value.approval_stages.push(newStage);
  newStage.save();
};

const deleteApprovalStage = (stage: AssetManagementPlanMasterApprovalStage) => {
  $confirm.open({
    onConfirm: () => {
      stage.deleteObject();
      currentPlanMaster.value.approval_stages =
        currentPlanMaster.value.approval_stages.filter(
          (s) => s.id !== stage.id,
        );
      currentPlanMaster.value.approval_stages.forEach((s, i) => {
        s.order = i;
      });
    },
  });
};

const editApproversDialog = ref(false);
const currentApprovalStage = ref<AssetManagementPlanMasterApprovalStage>(
  new AssetManagementPlanMasterApprovalStage(),
);

const openEditApproversDialog = (
  stage: AssetManagementPlanMasterApprovalStage,
) => {
  editApproversDialog.value = true;
  currentApprovalStage.value = stage;
};

const formatApprovers = (stage: AssetManagementPlanMasterApprovalStage) => {
  const names: string[] = [];
  for (const approver of stage.approvers) {
    const user = users.value.find((user) => user.id === approver.user_id);
    if (user) {
      names.push(user.name);
    }
  }
  return names.join(", ");
};

const getApprover = (user: User) => {
  return currentApprovalStage.value.approvers.find(
    (approver) => approver.user_id === user.id,
  );
};

const disableApprovers = ref(false);
const toggleApprover = async (user: User) => {
  disableApprovers.value = true;
  const approver = getApprover(user);
  if (approver) {
    currentApprovalStage.value.approvers =
      currentApprovalStage.value.approvers.filter((a) => a.id !== approver.id);
    await approver.deleteObject();
  } else {
    const newApprover = new AssetManagementPlanMasterApprover();
    newApprover.user_id = user.id;
    newApprover.stage_id = currentApprovalStage.value.id;
    currentApprovalStage.value.approvers.push(newApprover);
    await newApprover.save();
  }
  disableApprovers.value = false;
};

const revisionsDialog = ref(false);
const openRevisionsDialog = (planMaster: AssetManagementPlanMaster) => {
  revisionsDialog.value = true;
  currentPlanMaster.value = planMaster;
};

const downloadPdf = async (plan: AssetManagementPlan) => {
  const data = await axiosInstance.$get(
    `/report/asset-management-plan?id=${plan.id}`,
    {
      responseType: "blob",
    },
  );

  const url = window.URL.createObjectURL(
    new Blob([data], { type: "application/pdf" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "asset_management_plan.pdf");
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const compareRevisionsDialog = ref<InstanceType<
  typeof CompareRevisionsDialog
> | null>(null);

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_table = _resolveComponent("v-table")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_chip = _resolveComponent("v-chip")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(PaginatedTable, {
      ref_key: "table",
      ref: table,
      table: "asset-management-plans",
      "table-class": _unref(AssetManagementPlanMaster),
      "filter-definitions": filterDefinitions,
      headers: headers
    }, {
      "right-actions": _withCtx(() => [
        _createVNode(_component_v_btn, {
          "aria-label": "Add New Asset Management Plan",
          title: "Add New",
          color: "secondary",
          class: "mr-2",
          flat: "",
          disabled: !!_unref(loading),
          onClick: _cache[0] || (_cache[0] = ($event: any) => (newDialog.value = true))
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_icon, null, {
              default: _withCtx(() => [
                _createTextVNode("mdi-plus")
              ]),
              _: 1
            }),
            _createTextVNode("New ")
          ]),
          _: 1
        }, 8, ["disabled"])
      ]),
      "item.latest_published_timestamp": _withCtx(({
          item,
        }: {
          item: AssetManagementPlanMaster,
        }) => [
        _createTextVNode(_toDisplayString(item.latest_published_timestamp?.toLocaleString() ?? "N/A"), 1)
      ]),
      "item.actions": _withCtx(({ item }: { item: AssetManagementPlanMaster }) => [
        _createVNode(_component_v_tooltip, {
          location: "bottom",
          text: "Open Latest Revision"
        }, {
          activator: _withCtx(({ props }) => [
            _createVNode(_component_v_btn, _mergeProps(props, {
              icon: "mdi-arrow-right",
              flat: "",
              disabled: !!_unref(loading),
              density: "compact",
              onClick: ($event: any) => (
                _unref($router).push(
                  `/asset-management-plan?id=${item.metrics.latest_revision_id}`,
                )
              )
            }), null, 16, ["disabled", "onClick"])
          ]),
          _: 2
        }, 1024),
        _createVNode(_component_v_tooltip, {
          location: "bottom",
          text: "View Approval Stages"
        }, {
          activator: _withCtx(({ props }) => [
            _createVNode(_component_v_btn, _mergeProps(props, {
              icon: "mdi-account-check",
              flat: "",
              disabled: !!_unref(loading),
              density: "compact",
              onClick: ($event: any) => (
                openApprovalStagesDialog(item as AssetManagementPlanMaster)
              )
            }), null, 16, ["disabled", "onClick"])
          ]),
          _: 2
        }, 1024),
        _createVNode(_component_v_tooltip, {
          location: "bottom",
          text: "View Revisions"
        }, {
          activator: _withCtx(({ props }) => [
            _createVNode(_component_v_btn, _mergeProps(props, {
              icon: "mdi-timeline-text",
              flat: "",
              disabled: !!_unref(loading),
              density: "compact",
              onClick: ($event: any) => (openRevisionsDialog(item as AssetManagementPlanMaster))
            }), null, 16, ["disabled", "onClick"])
          ]),
          _: 2
        }, 1024)
      ]),
      _: 1
    }, 8, ["table-class"]),
    _createVNode(BaseDialog, {
      modelValue: newDialog.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newDialog).value = $event)),
      maxWidth: "500",
      title: "New Plan"
    }, {
      actions: _withCtx(() => [
        _createVNode(_component_v_btn, {
          "aria-label": "Add New Asset Management Plan",
          title: "Add New",
          color: "secondary",
          flat: "",
          disabled: !newPlanDetails.value.name,
          loading: !!_unref(loading),
          onClick: _cache[2] || (_cache[2] = ($event: any) => (newAssetManagementPlan()))
        }, {
          default: _withCtx(() => [
            _createTextVNode(" Add New ")
          ]),
          _: 1
        }, 8, ["disabled", "loading"])
      ]),
      default: _withCtx(() => [
        _createVNode(_component_v_text_field, {
          modelValue: newPlanDetails.value.name,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newPlanDetails.value.name) = $event)),
          variant: "outlined",
          density: "compact",
          "aria-label": "Asset Managment Plan Name",
          label: "Name"
        }, null, 8, ["modelValue"])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(BaseDialog, {
      modelValue: approvalStagesDialog.value,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((approvalStagesDialog).value = $event)),
      "max-width": "600",
      title: "Approval Stages"
    }, {
      actions: _withCtx(() => [
        _createVNode(_component_v_btn, {
          color: "secondary",
          variant: "outlined",
          onClick: addApprovalStage
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_icon, null, {
              default: _withCtx(() => [
                _createTextVNode("mdi-plus")
              ]),
              _: 1
            }),
            _createTextVNode("Stage ")
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(_component_v_table, null, {
          default: _withCtx(() => [
            _hoisted_1,
            _createVNode(_unref(draggable), {
              modelValue: currentPlanMaster.value.approval_stages,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((currentPlanMaster.value.approval_stages) = $event)),
              handle: ".handle",
              tag: "tbody",
              "item-key": "id",
              onChange: _cache[5] || (_cache[5] = ($event: any) => (currentPlanMaster.value.reorderStages()))
            }, {
              item: _withCtx(({
              element,
            }: {
              element: AssetManagementPlanMasterApprovalStage,
            }) => [
                _createElementVNode("tr", null, [
                  _createElementVNode("td", null, [
                    _createVNode(_component_v_icon, {
                      class: "handle",
                      icon: "mdi-drag-horizontal"
                    })
                  ]),
                  _createElementVNode("td", null, _toDisplayString(formatApprovers(element)), 1),
                  _createElementVNode("td", null, [
                    _createVNode(_component_v_btn, {
                      icon: "mdi-pencil",
                      flat: "",
                      density: "compact",
                      onClick: ($event: any) => (openEditApproversDialog(element))
                    }, null, 8, ["onClick"]),
                    _createVNode(_component_v_btn, {
                      icon: "mdi-delete",
                      flat: "",
                      density: "compact",
                      onClick: ($event: any) => (deleteApprovalStage(element))
                    }, null, 8, ["onClick"])
                  ])
                ])
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(BaseDialog, {
      modelValue: editApproversDialog.value,
      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((editApproversDialog).value = $event)),
      "max-width": "400",
      title: "Select Approvers"
    }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(users), (user) => {
          return (_openBlock(), _createBlock(_component_v_checkbox, {
            key: user.id,
            "model-value": !!getApprover(user),
            label: user.name,
            density: "compact",
            disabled: disableApprovers.value,
            "hide-details": "",
            "onUpdate:modelValue": ($event: any) => (toggleApprover(user))
          }, null, 8, ["model-value", "label", "disabled", "onUpdate:modelValue"]))
        }), 128))
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(BaseDialog, {
      modelValue: revisionsDialog.value,
      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((revisionsDialog).value = $event)),
      "max-width": "650",
      title: "Revisions"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_table, null, {
          default: _withCtx(() => [
            _hoisted_2,
            _createElementVNode("tbody", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(currentPlanMaster.value.asset_management_plans, (plan) => {
                return (_openBlock(), _createElementBlock("tr", {
                  key: plan.id
                }, [
                  _createElementVNode("td", null, _toDisplayString(plan.revision), 1),
                  _createElementVNode("td", null, [
                    _createVNode(_component_v_chip, {
                      color: plan.status_color,
                      text: plan.status,
                      label: ""
                    }, null, 8, ["color", "text"])
                  ]),
                  _createElementVNode("td", null, _toDisplayString(plan.created.toLocaleString()), 1),
                  _createElementVNode("td", null, [
                    _createVNode(_component_v_tooltip, {
                      location: "bottom",
                      text: "Open Revision"
                    }, {
                      activator: _withCtx(({ props }) => [
                        _createVNode(_component_v_btn, _mergeProps({ ref_for: true }, props, {
                          icon: "mdi-arrow-right",
                          flat: "",
                          density: "compact",
                          onClick: ($event: any) => (
                      _unref($router).push(`/asset-management-plan?id=${plan.id}`)
                    )
                        }), null, 16, ["onClick"])
                      ]),
                      _: 2
                    }, 1024),
                    _createVNode(_component_v_tooltip, {
                      location: "bottom",
                      text: "Download PDF"
                    }, {
                      activator: _withCtx(({ props }) => [
                        _createVNode(_component_v_btn, _mergeProps({ ref_for: true }, props, {
                          icon: "mdi-printer-outline",
                          flat: "",
                          density: "compact",
                          disabled: 
                      plan.status !== 'Draft' && plan.status !== 'Published'
                    ,
                          onClick: ($event: any) => (downloadPdf(plan))
                        }), null, 16, ["disabled", "onClick"])
                      ]),
                      _: 2
                    }, 1024),
                    _createVNode(_component_v_tooltip, {
                      location: "bottom",
                      text: "Compare Revisions"
                    }, {
                      activator: _withCtx(({ props }) => [
                        _createVNode(_component_v_btn, _mergeProps({ ref_for: true }, props, {
                          icon: "mdi-file-compare",
                          flat: "",
                          density: "compact",
                          onClick: ($event: any) => (compareRevisionsDialog.value?.open(plan))
                        }), null, 16, ["onClick"])
                      ]),
                      _: 2
                    }, 1024)
                  ])
                ]))
              }), 128))
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(CompareRevisionsDialog, {
      ref_key: "compareRevisionsDialog",
      ref: compareRevisionsDialog,
      master: currentPlanMaster.value
    }, null, 8, ["master"])
  ]))
}
}

})